import { PHONE_TYPE_LABEL, asPhoneType, isCallable } from './phoneList.js';

/**
 * What a CRM plan will do, in a person's words, read from the plan itself.
 *
 * The confirm screen used to list field NAMES ("emails, phones, givenName…") and leave a
 * person to remember the values from the screen before. This turns the server's own
 * proposed operations — the plan the confirmation is bound to — into the values that will
 * be sent. It never reads the form: a value that is not in the plan is not shown as being
 * sent, and a value in the plan is never left out.
 *
 * Formatting only. Nothing here may change what a value means.
 */

export interface PlanOperation {
  intent: string;
  targetExternalId: string | null;
  fields: Readonly<Record<string, unknown>>;
  updateMask: readonly string[];
  tagKeys: readonly string[];
}

export interface PlanPhone {
  /** Exactly as it will be sent. */
  e164: string;
  /** For reading: a North American number grouped, anything else as sent. */
  display: string;
  typeLabel: string;
  /** Keap's first phone slot. Only claimed for a new contact, where it is certain. */
  primary: boolean;
}

export type PlanActionKind = 'create' | 'update' | 'tags' | 'none';

export interface PlanSummary {
  action: PlanActionKind;
  /** The one line that says what happens in Keap. */
  title: string;
  fullName: string | null;
  givenName: string | null;
  familyName: string | null;
  jobTitle: string | null;
  company: string | null;
  emails: string[];
  phones: PlanPhone[];
  /** The address as it will be sent, as lines to print. Empty when no address is sent. */
  addressLines: string[];
  website: string | null;
  /** Keap's own Contact Type field, as sent. Never a tag. */
  contactType: string | null;
  /** Tag keys, in the order a person chose them. Labels are the caller's to resolve. */
  tagKeys: string[];
  /** A new contact's tags go to Keap in their own request, once the contact exists. */
  tagsSeparately: boolean;
}

const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

/** +17038248841 → +1 (703) 824-8841. Anything else is shown exactly as it will be sent. */
export function displayPhone(e164: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `+1 (${m[1]}) ${m[2]}-${m[3]}` : e164;
}

/** Street lines, then "City, State Postcode", then the country — only what is present. */
export function addressLines(a: unknown): string[] {
  if (!a || typeof a !== 'object') return [];
  const v = a as Record<string, unknown>;
  const place = [text(v.locality), [text(v.region), text(v.postalCode)].filter(Boolean).join(' ') || null]
    .filter(Boolean).join(', ');
  return [text(v.line1), text(v.line2), place || null, text(v.countryCode)].filter((x): x is string => Boolean(x));
}

export function summarizePlan(ops: readonly PlanOperation[]): PlanSummary {
  const write = ops.find((o) => o.intent === 'create_contact') ?? ops.find((o) => o.intent === 'update_contact');
  const f = write?.fields ?? {};
  const action: PlanActionKind = write?.intent === 'create_contact' ? 'create'
    : write ? 'update'
      : ops.some((o) => o.intent === 'apply_tags' && o.tagKeys.length > 0) ? 'tags' : 'none';

  const phones = Array.isArray(f.phones) ? f.phones as Array<{ e164?: unknown; type?: unknown }> : [];
  const firstCallable = phones.findIndex((p) => isCallable(asPhoneType(text(p.type))));
  const given = text(f.givenName);
  const family = text(f.familyName);
  const tagKeys = [...new Set(ops.flatMap((o) => o.tagKeys))];

  return {
    action,
    title: action === 'create' ? 'Create new contact'
      : action === 'update' ? 'Update existing contact'
        : action === 'tags' ? 'Add CRM Tags to the existing contact'
          : 'Nothing to send',
    fullName: [given, family].filter(Boolean).join(' ') || null,
    givenName: given,
    familyName: family,
    jobTitle: text(f.jobTitle),
    company: text(f.company),
    emails: Array.isArray(f.emails) ? (f.emails as unknown[]).map(text).filter((e): e is string => Boolean(e)) : [],
    phones: phones.flatMap((p, i) => {
      const e164 = text(p.e164);
      if (!e164) return [];
      return [{
        e164, display: displayPhone(e164),
        typeLabel: PHONE_TYPE_LABEL[asPhoneType(text(p.type))],
        primary: action === 'create' && i === firstCallable,
      }];
    }),
    addressLines: addressLines(f.address),
    website: text(f.website),
    contactType: text(f.contactType),
    tagKeys,
    tagsSeparately: tagKeys.length > 0 && action === 'create',
  };
}
